import useSWR from 'swr';
import { fetcher } from '../../services/fetcher';

export interface FilterPreset {
  id: number;
  userId: number;
  moduleName: string;
  name: string;
  description?: string;
  filters: Record<string, any>;
  isDefault: boolean;
  showInQuickFilters: boolean;
  isPublic?: boolean;
  sharedWithUsers?: number[] | null;
  createdAt: string;
  updatedAt: string;
  user?: {
    id: number;
    userDisplayName?: string;
    userFirstName?: string;
    userLastName?: string;
    userEmail?: string;
  };
}

export interface TablePreset {
  id: number;
  userId: number;
  moduleName: string;
  name: string;
  description?: string;
  config: Record<string, any>;
  isDefault: boolean;
  isPublic?: boolean;
  sharedWithUsers?: number[] | null;
  createdAt: string;
  updatedAt: string;
  user?: {
    id: number;
    userDisplayName?: string;
    userFirstName?: string;
    userLastName?: string;
    userEmail?: string;
  };
}

export interface ConditionalFormattingRule {
  id: number;
  userId: number;
  moduleName: string;
  columnKey: string;
  name: string;
  description?: string;
  scope: string;
  condition: Record<string, any>;
  style: Record<string, any>;
  isActive: boolean;
  priority: number;
  isDefault: boolean;
  isPublic?: boolean;
  sharedWithUsers?: number[] | null;
  createdAt: string;
  updatedAt: string;
  user?: {
    id: number;
    userDisplayName?: string;
    userFirstName?: string;
    userLastName?: string;
    userEmail?: string;
  };
}

// Hook to get users for sharing
export const useUsersForSelect = () => {
  const { data, error, isLoading } = useSWR<any[]>(
    '/users',
    fetcher,
    {
      revalidateOnFocus: false,
    }
  );

  return {
    users: data || [],
    isLoading,
    isError: error,
    options: (data || []).map((user) => ({
      value: user.id,
      label: user.userDisplayName || `${user.userFirstName || ''} ${user.userLastName || ''}`.trim() || `User #${user.id}`,
    })),
  };
};

// Filter Presets hooks
export const useFilterPresets = (moduleName?: string, includeAll: boolean = true) => {
  const params = new URLSearchParams();
  if (moduleName) params.append('moduleName', moduleName);
  if (includeAll) params.append('all', 'true');
  const queryString = params.toString() ? `?${params.toString()}` : '';
  
  const { data, error, isLoading, mutate } = useSWR<FilterPreset[]>(
    `/filter-presets${queryString}`,
    fetcher,
    {
      revalidateOnFocus: true,
    }
  );

  return {
    filterPresets: data || [],
    isLoading,
    isError: error,
    mutate,
  };
};

// Table Presets hooks
export const useTablePresets = (moduleName?: string, includeAll: boolean = true) => {
  const params = new URLSearchParams();
  if (moduleName) params.append('moduleName', moduleName);
  if (includeAll) params.append('all', 'true');
  const queryString = params.toString() ? `?${params.toString()}` : '';
  
  const { data, error, isLoading, mutate } = useSWR<TablePreset[]>(
    `/table-presets${queryString}`,
    fetcher,
    {
      revalidateOnFocus: true,
    }
  );

  return {
    tablePresets: data || [],
    isLoading,
    isError: error,
    mutate,
  };
};

// Conditional Formatting Rules hooks
export const useConditionalFormattingRules = (moduleName?: string, includeAll: boolean = true) => {
  const params = new URLSearchParams();
  if (moduleName) params.append('moduleName', moduleName);
  if (includeAll) params.append('all', 'true');
  const queryString = params.toString() ? `?${params.toString()}` : '';
  
  const { data, error, isLoading, mutate } = useSWR<ConditionalFormattingRule[]>(
    `/conditional-formatting${queryString}`,
    fetcher,
    {
      revalidateOnFocus: true,
    }
  );

  return {
    rules: data || [],
    isLoading,
    isError: error,
    mutate,
  };
};

